import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventIcon from '@mui/icons-material/Event';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SearchIcon from '@mui/icons-material/Search';

import { BrowserTitle, Spinner } from '../components';
import fixRequestServices from '../services/fixRequestServices';
import { moment, formatDateTime } from '../utils/dateUtils';

const STATUS_COLORS = {
  Waiting: 'warning',
  Scheduled: 'info',
  'In Progress': 'secondary',
  Done: 'success',
  Cancelled: 'default',
};

const PRIORITY_COLORS = {
  High: 'error',
  Medium: 'warning',
  Low: 'default',
};

const ACTIVE_STATUSES = 'Waiting,Scheduled,In Progress';

const formatDay = (value) => {
  if (!value) return '—';
  const str = String(value).split('T')[0];
  const parsed = moment(str, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : '—';
};

// Wait List: post-completion rework requests (free fixes on existing
// contracts + paid rework contracts parked for manager scheduling).
// Access: admins or users with the `wait_list` permission.
export const WaitList = () => {
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState('Active');
  const [typeFilter, setTypeFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // Contract preview modal (same read-only view as /proposals/view/:id)
  const [viewProposalId, setViewProposalId] = useState(null);

  // Detail / schedule dialog
  const [detail, setDetail] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    date: '',
    installationTime: 'Full Day',
    installersCount: 2,
    trucksCount: 1,
  });
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [acting, setActing] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const status =
        statusFilter === 'Active' ? ACTIVE_STATUSES : statusFilter === 'All' ? '' : statusFilter;
      const type = typeFilter === 'All' ? '' : typeFilter;
      const response = await fixRequestServices.getList(
        page * rowsPerPage,
        rowsPerPage,
        search,
        status,
        type,
      );
      const payload = response.data?.value || response.data || {};
      setRows(payload.data || []);
      setTotal(payload.total ?? (payload.data || []).length);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Wait List access required');
      } else {
        toast.error('Failed to load Wait List');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, typeFilter, search]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openDetail = async (id) => {
    try {
      const response = await fixRequestServices.getById(id);
      setDetail(response.data);
    } catch (err) {
      toast.error('Failed to load request');
      console.error(err);
    }
  };

  const refreshAfterAction = async (id) => {
    await loadRows();
    if (id) {
      try {
        const response = await fixRequestServices.getById(id);
        setDetail(response.data);
      } catch {
        setDetail(null);
      }
    }
  };

  const handleOpenSchedule = () => {
    setScheduleForm({
      date: detail?.scheduledDate ? String(detail.scheduledDate).split('T')[0] : '',
      installationTime: detail?.scheduledTime || 'Full Day',
      installersCount: detail?.installersCount || 2,
      trucksCount: detail?.trucksCount ?? 1,
    });
    setScheduleOpen(true);
  };

  const handleSchedule = async () => {
    if (!scheduleForm.date) {
      toast.error('Pick a date');
      return;
    }
    setActing(true);
    try {
      await fixRequestServices.schedule(detail.id, {
        date: scheduleForm.date,
        installationTime: scheduleForm.installationTime,
        installersCount: Number(scheduleForm.installersCount) || 1,
        trucksCount: Number(scheduleForm.trucksCount) || 1,
      });
      toast.success('Visit scheduled — Fix job created in Delivery City');
      setScheduleOpen(false);
      await refreshAfterAction(detail.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to schedule');
      console.error(err);
    } finally {
      setActing(false);
    }
  };

  const handleResolve = async () => {
    setActing(true);
    try {
      await fixRequestServices.resolve(detail.id, resolutionNotes.trim() || undefined);
      toast.success('Request marked as done');
      setResolveOpen(false);
      setResolutionNotes('');
      await refreshAfterAction(detail.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to resolve');
      console.error(err);
    } finally {
      setActing(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    const action = async () => {
      setActing(true);
      try {
        await fixRequestServices.changeStatus(detail.id, newStatus);
        toast.success(`Status changed to ${newStatus}`);
        await refreshAfterAction(detail.id);
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to change status');
        console.error(err);
      } finally {
        setActing(false);
      }
    };

    if (newStatus === 'Cancelled') {
      confirm({
        title: 'Cancel this request?',
        description: `Request ${detail.requestNumber} will be marked as Cancelled.`,
      })
        .then(action)
        .catch(() => {});
    } else if (newStatus === 'Waiting') {
      confirm({
        title: 'Reset to Waiting?',
        description: `Request ${detail.requestNumber} will go back to Waiting. The scheduled date will be cleared — assign a new visit date when ready.`,
      })
        .then(action)
        .catch(() => {});
    } else {
      action();
    }
  };

  const proposalLinkFor = (request) => {
    const proposal = request.paidProposal || request.originalProposal;
    return proposal ? `/proposals/${proposal.id}` : null;
  };

  const userName = (user) => {
    if (!user) return null;
    return user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || null;
  };

  const addressFor = (request) => {
    const property = request.property;
    if (!property) return '—';
    return (
      property.place?.label ||
      property.sourceAddress ||
      [property.streetNumber, property.streetName, property.city].filter(Boolean).join(' ') ||
      '—'
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <BrowserTitle title="Wait List | Airmaxx Pro" />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Wait List
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search: number, address, description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                setSearch(searchInput.trim());
              }
            }}
            sx={{ minWidth: 260 }}
          />
          <IconButton
            onClick={() => {
              setPage(0);
              setSearch(searchInput.trim());
            }}
          >
            <SearchIcon />
          </IconButton>

          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            size="small"
            onChange={(e, value) => {
              if (value) {
                setTypeFilter(value);
                setPage(0);
              }
            }}
          >
            <ToggleButton value="All">All types</ToggleButton>
            <ToggleButton value="Free Fix">Free Fix</ToggleButton>
            <ToggleButton value="Paid Work">Paid Work</ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            size="small"
            onChange={(e, value) => {
              if (value) {
                setStatusFilter(value);
                setPage(0);
              }
            }}
          >
            <ToggleButton value="Active">Active</ToggleButton>
            <ToggleButton value="Waiting">Waiting</ToggleButton>
            <ToggleButton value="Scheduled">Scheduled</ToggleButton>
            <ToggleButton value="Done">Done</ToggleButton>
            <ToggleButton value="All">All</ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Refresh">
            <IconButton onClick={loadRows}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Spinner />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Number</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Scheduled</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No requests found
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((request) => (
                  <TableRow
                    key={request.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openDetail(request.id)}
                  >
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {request.type === 'Paid Work' && request.paidProposal?.id ? (
                        <Link
                          component="button"
                          underline="hover"
                          sx={{ fontWeight: 600 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewProposalId(request.paidProposal.id);
                          }}
                        >
                          {request.requestNumber}
                        </Link>
                      ) : (
                        request.requestNumber
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={request.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={request.status} size="small" color={STATUS_COLORS[request.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={request.priority} size="small" color={PRIORITY_COLORS[request.priority] || 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {addressFor(request)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {request.issueDescription}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDay(request.scheduledDate)}
                      {request.scheduledTime && request.scheduledDate ? ` (${request.scheduledTime})` : ''}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDay(request.createdAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {userName(request.createdBy) || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* ===== Detail dialog ===== */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <span>{detail.requestNumber}</span>
                <Chip label={detail.type} size="small" variant="outlined" />
                <Chip label={detail.status} size="small" color={STATUS_COLORS[detail.status] || 'default'} />
                <Chip label={detail.priority} size="small" color={PRIORITY_COLORS[detail.priority] || 'default'} variant="outlined" />
              </Box>
              <IconButton onClick={() => setDetail(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Address:</strong> {addressFor(detail)}
              </Typography>
              {detail.originalProposal && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Original contract:</strong>{' '}
                  <Link component={RouterLink} to={`/proposals/${detail.originalProposal.id}`}>
                    {detail.originalProposal.publicId}
                  </Link>
                </Typography>
              )}
              {detail.paidProposal && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Paid contract:</strong>{' '}
                  <Link component="button" underline="hover" onClick={() => setViewProposalId(detail.paidProposal.id)}>
                    {detail.paidProposal.publicId}
                  </Link>
                </Typography>
              )}
              {detail.deliveryJobId && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Delivery City job:</strong> #{detail.deliveryJobId}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Created:</strong> {formatDateTime(detail.createdAt)} by{' '}
                {userName(detail.createdBy) || '—'}
              </Typography>
              {detail.scheduledDate && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Scheduled:</strong> {formatDay(detail.scheduledDate)} ({detail.scheduledTime}) —{' '}
                  {detail.installersCount} installer(s) / {detail.trucksCount} truck(s)
                </Typography>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Problem / work to be done
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
                {detail.issueDescription}
              </Typography>

              {detail.resolutionNotes && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Resolution
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
                    {detail.resolutionNotes}
                  </Typography>
                </>
              )}

              {(detail.activities || []).length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Activity
                  </Typography>
                  {detail.activities.map((activity) => (
                    <Box key={activity.id} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(activity.createdAt)}
                      </Typography>
                      <Typography variant="caption">
                        <strong>{userName(activity.user) || 'System'}:</strong>{' '}
                        {activity.description || activity.action}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
              {detail.type === 'Free Fix' &&
                ['Waiting', 'Scheduled', 'In Progress'].includes(detail.status) && (
                  <Button variant="contained" startIcon={<EventIcon />} onClick={handleOpenSchedule} disabled={acting}>
                    {detail.status === 'Waiting' ? 'Schedule Visit' : 'Reschedule'}
                  </Button>
                )}
              {detail.type === 'Paid Work' && detail.status === 'Waiting' && detail.paidProposal && (
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={`/proposals/${detail.paidProposal.id}`}
                  startIcon={<EventIcon />}
                >
                  Set dates on contract
                </Button>
              )}
              {['Waiting', 'Scheduled'].includes(detail.status) && (
                <Button onClick={() => handleStatusChange('In Progress')} disabled={acting}>
                  In Progress
                </Button>
              )}
              {['Waiting', 'Scheduled', 'In Progress'].includes(detail.status) && (
                <>
                  <Button
                    color="success"
                    variant="outlined"
                    startIcon={<DoneAllIcon />}
                    onClick={() => setResolveOpen(true)}
                    disabled={acting}
                  >
                    Done
                  </Button>
                  <Button color="error" onClick={() => handleStatusChange('Cancelled')} disabled={acting}>
                    Cancel Request
                  </Button>
                </>
              )}
              {['Scheduled', 'In Progress', 'Done'].includes(detail.status) && (
                <Button color="warning" onClick={() => handleStatusChange('Waiting')} disabled={acting}>
                  Reset to Waiting
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ===== Schedule dialog (free fixes) ===== */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule visit — {detail?.requestNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A Fix job will be created in Delivery City with the same F-number.
          </Typography>
          <TextField
            label="Date"
            type="date"
            fullWidth
            required
            value={scheduleForm.date}
            onChange={(e) => setScheduleForm((prev) => ({ ...prev, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Time slot"
            select
            fullWidth
            value={scheduleForm.installationTime}
            onChange={(e) => setScheduleForm((prev) => ({ ...prev, installationTime: e.target.value }))}
            sx={{ mb: 2 }}
          >
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
            <MenuItem value="Full Day">Full Day</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Installers"
              type="number"
              fullWidth
              value={scheduleForm.installersCount}
              onChange={(e) => setScheduleForm((prev) => ({ ...prev, installersCount: e.target.value }))}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Trucks"
              type="number"
              fullWidth
              value={scheduleForm.trucksCount}
              onChange={(e) => setScheduleForm((prev) => ({ ...prev, trucksCount: e.target.value }))}
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSchedule} disabled={acting}>
            {acting ? 'Scheduling...' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Resolve dialog ===== */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark {detail?.requestNumber} as Done</DialogTitle>
        <DialogContent>
          <TextField
            label="Resolution notes (optional)"
            multiline
            minRows={3}
            fullWidth
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleResolve} disabled={acting}>
            {acting ? 'Saving...' : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Contract preview modal (same view as /proposals/view/:id) ===== */}
      <Dialog
        open={Boolean(viewProposalId)}
        onClose={() => setViewProposalId(null)}
        maxWidth="xl"
        fullWidth
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
          <IconButton onClick={() => setViewProposalId(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, height: '85vh' }}>
          {viewProposalId && (
            <iframe
              src={`/proposals/view/${viewProposalId}`}
              title="Proposal view"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WaitList;
